// The company details form, used to register a company (FR-40) and by a
// company_admin to edit its details.
import { Button, Group, NativeSelect, SimpleGrid, Stack, TextInput } from "@mantine/core";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type { Schemas } from "../../api/client";
import { FormError } from "../../shared/ui/Page";
import { SEGMENTS, type Company, type Segment } from "./common";

export type CompanyDetails = Schemas["CompanyUpdate"];

type Draft = {
  name: string;
  segment: Segment;
  registration_number: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
};

function draftOf(c?: Company): Draft {
  return {
    name: c?.name ?? "",
    segment: (SEGMENTS as string[]).includes(c?.segment ?? "") ? (c?.segment as Segment) : "other",
    registration_number: c?.registration_number ?? "",
    contact_name: c?.contact_name ?? "",
    contact_email: c?.contact_email ?? "",
    contact_phone: c?.contact_phone ?? "",
  };
}

/** toDetails trims the form and leaves out empty optional fields. */
export function toDetails(d: Draft): CompanyDetails {
  const reg = d.registration_number.trim();
  const phone = d.contact_phone.trim();
  return {
    name: d.name.trim(),
    segment: d.segment,
    contact_name: d.contact_name.trim(),
    contact_email: d.contact_email.trim(),
    ...(reg ? { registration_number: reg } : {}),
    ...(phone ? { contact_phone: phone } : {}),
  };
}

export function CompanyForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: Company;
  submitLabel: string;
  /** onSubmit returns an error message to show, or null on success. */
  onSubmit: (d: CompanyDetails) => Promise<string | null>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Draft>(() => draftOf(initial));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.currentTarget.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setError(await onSubmit(toDetails(form)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="company-form">
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label={t("business.form_name")}
            value={form.name}
            onChange={set("name")}
            required
            maxLength={200}
          />
          <NativeSelect
            label={t("business.form_segment")}
            value={form.segment}
            onChange={set("segment")}
            data={SEGMENTS.map((s) => ({ value: s, label: t(`segment.${s}`) }))}
          />
          <TextInput
            label={t("business.form_registration")}
            value={form.registration_number}
            onChange={set("registration_number")}
          />
          <TextInput
            label={t("business.form_contact_name")}
            value={form.contact_name}
            onChange={set("contact_name")}
            required
          />
          <TextInput
            type="email"
            label={t("business.form_contact_email")}
            value={form.contact_email}
            onChange={set("contact_email")}
            required
          />
          <TextInput
            type="tel"
            label={t("business.form_contact_phone")}
            value={form.contact_phone}
            onChange={set("contact_phone")}
          />
        </SimpleGrid>
        <FormError message={error} />
        <Group gap="xs">
          <Button type="submit" loading={busy}>
            {submitLabel}
          </Button>
          <Button variant="default" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
